import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AppDispatch, RootState } from '../../../store/store';
import { submitConsent } from '../store/consentSlice';
import './ConsentAcceptance.css';

const CONSENT_VERSION = '1.0';

const CONSENT_TYPES = [
  {
    type: 'PARENTAL_GENERAL',
    label: 'General Parental Consent',
    description:
      'I consent to my child participating in educational activities on the IQMaxx platform, including live sessions with verified teachers.',
  },
  {
    type: 'DATA_COLLECTION',
    label: 'Data Collection & Privacy',
    description:
      "I consent to the collection of my child's educational data (attendance, progress, rubric scores) for the purpose of delivering and improving the educational experience. Data is handled in accordance with COPPA requirements.",
  },
  {
    type: 'PLATFORM_USAGE',
    label: 'Platform Usage & Recording',
    description:
      'I consent to session recordings for child safety purposes. Recordings are stored securely and accessible only to authorized parties.',
  },
];

const ConsentAcceptance: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('childId') || '';

  const { isLoading, error } = useSelector((state: RootState) => state.consent);

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);

  const allChecked = CONSENT_TYPES.every((ct) => checked[ct.type]);

  const handleToggle = (type: string) => {
    setChecked((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!childId) return;

    try {
      for (const ct of CONSENT_TYPES) {
        if (checked[ct.type]) {
          await dispatch(
            submitConsent({
              childId,
              consentType: ct.type,
              version: CONSENT_VERSION,
            }),
          ).unwrap();
        }
      }
      setSubmitted(true);
      setTimeout(() => navigate(-1), 1500);
    } catch {
      // Error handled by Redux slice
    }
  };

  if (!childId) {
    return (
      <div className="consent-acceptance">
        <h2>Parental Consent</h2>
        <p className="consent-error">
          No child selected. Please select a child from your profile first.
        </p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="consent-acceptance">
        <div className="consent-success">
          <h2>Consent Granted</h2>
          <p>Your consent has been recorded. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="consent-acceptance">
      <h2>Parental Consent Required</h2>
      <p className="consent-intro">
        Before your child can participate in IQMaxx activities, we need your consent for the
        following:
      </p>

      <form onSubmit={handleSubmit}>
        {CONSENT_TYPES.map((ct) => (
          <label key={ct.type} className="consent-item">
            <input
              type="checkbox"
              checked={checked[ct.type] || false}
              onChange={() => handleToggle(ct.type)}
              disabled={isLoading}
            />
            <div className="consent-item-text">
              <strong>{ct.label}</strong>
              <p>{ct.description}</p>
            </div>
          </label>
        ))}

        {error && <p className="consent-error">{error}</p>}

        <p className="consent-version">Consent Version: {CONSENT_VERSION}</p>

        <button type="submit" className="consent-submit" disabled={!allChecked || isLoading}>
          {isLoading ? 'Submitting...' : 'I Agree'}
        </button>
      </form>
    </div>
  );
};

export default ConsentAcceptance;
